import React, { useEffect, useState } from 'react';
import { authAPI } from '../services/api';
import useStore from '../store/useStore';
import '../styles/UserProfile.css';
import { CodeInput, RecoveryCodes } from './TwoFactor';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

/** Two-step verification: on since when, recovery codes left, and new recovery codes for a code from the app. */
function TwoStepSection() {
  const [status, setStatus] = useState(null);
  const [asking, setAsking] = useState(false);
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = () => authAPI.twoFactorStatus().then((r) => setStatus(r.data)).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const renew = async (code) => {
    setBusy(true);
    setError('');
    try {
      setCodes((await authAPI.newRecoveryCodes(code)).data.recoveryCodes);
      setAsking(false);
    } catch (err) {
      setError(describeError(err, 'That code did not work. Try the newest code.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="profile-section two-step">
      <h2 className="section-title">Two-step verification</h2>
      {status && (
        <p className="two-step-status">
          <span className="two-step-badge">On</span>
          {status.enabledAt && ` since ${new Date(status.enabledAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}`}
          {` · ${status.recoveryCodesLeft} recovery code${status.recoveryCodesLeft === 1 ? '' : 's'} left`}
        </p>
      )}
      <p className="form-hint">Each sign-in needs a code from your authenticator app. If you lose your phone, sign in with a recovery code, or ask an admin to reset your two-step verification.</p>
      {codes ? (
        <RecoveryCodes codes={codes} doneLabel="Done" onDone={() => { setCodes(null); load(); }} />
      ) : asking ? (
        <div className="two-step-renew">
          <p className="form-hint">Enter a code from your authenticator app to create new recovery codes. The old ones will stop working.</p>
          {error && <div className="message error-message">{error}</div>}
          <CodeInput onComplete={renew} disabled={busy} />
          <button type="button" className="btn-secondary" onClick={() => { setAsking(false); setError(''); }}>Cancel</button>
        </div>
      ) : (
        <button type="button" className="btn-secondary" onClick={() => setAsking(true)}>Create new recovery codes</button>
      )}
    </div>
  );
}

const UserProfile = () => {
  const user = useStore((state) => state.user);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      setMessage('');
      return;
    }

    if (newPassword.length < 12) {
      setError('New password must be at least 12 characters long');
      setMessage('');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setMessage('');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await authAPI.changePassword(currentPassword, newPassword);

      if (response.success) {
        // Changing a password ends every earlier session, including this one's old token.
        const { token } = response.data || {};
        if (token) {
          localStorage.setItem('token', token);
          useStore.setState({ token });
        }
        setMessage('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(response.error || 'Failed to change password');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="user-profile-container">
      <h1 className="page-title">User Profile</h1>

      <div className="profile-section">
        <h2 className="section-title">Account Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Username</label>
            <div className="info-value">{user?.username}</div>
          </div>
          <div className="info-item">
            <label>Email</label>
            <div className="info-value">{user?.email}</div>
          </div>
          <div className="info-item">
            <label>Full Name</label>
            <div className="info-value">{user?.fullName || 'Not set'}</div>
          </div>
          <div className="info-item">
            <label>Role</label>
            <div className="info-value role-badge role-{user?.role}">
              {user?.role === 'super_admin' ? 'Super Admin' :
               user?.role === 'admin' ? 'Admin' : 'User'}
            </div>
          </div>
        </div>
      </div>

      <TwoStepSection />

      <div className="profile-section">
        <h2 className="section-title">Change Password</h2>
        <form onSubmit={handleChangePassword} className="password-form">
          {error && (
            <div className="message error-message">{error}</div>
          )}
          {message && (
            <div className="message success-message">{message}</div>
          )}

          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="new-password"
            />
            <small className="form-hint">Minimum 12 characters</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserProfile;
